import { ChevronDown, ChevronUp, GripVertical, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  type ArrayPath,
  Controller,
  type FieldPath,
  type FieldValues,
  type PathValue,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { type PropertyAttributeTypeId, type PropertySchemaType, propertyAttributeTypes } from "~/common/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "~/components/ui";
import {
  isMandatoryUnsavedSchema,
  MANDATORY_PROPERTY_MAPPINGS,
} from "~/features/admin/propertySchemas/utils/mandatoryMappings";
import { PropertyTypeSelect } from "./PropertyTypeSelect";
import { SelectOptionFormInput } from "./SelectOptionFormInput";
import { URLAllowListFormInput } from "./URLAllowListFormInput";

const NONE = "__none";

interface PropertySchemaAssociationFormInputProps {
  index: number;
  onRemove: (index: number) => void;
  showPropertyAttributeTypeId?: boolean;
  supportedPropertyTypes?: string[];
  sortMode?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

/**
 * One row of the property-schema editor. Handles the basic fields plus
 * the type-dependent sub-arrays (select options, URL allowlist). When the
 * row is a mandatory entity schema *and* not yet saved, type/attribute are
 * locked and the delete button is disabled.
 */
export function PropertySchemaAssociationFormInput({
  index,
  onRemove,
  showPropertyAttributeTypeId,
  supportedPropertyTypes,
  sortMode,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: PropertySchemaAssociationFormInputProps) {
  const { t } = useTranslation();
  const { register, control, getValues, setValue, watch, getFieldState, formState } = useFormContext();
  const basePath = `propertySchemaAssociations.${index}`;

  const paths = {
    id: `${basePath}.id`,
    name: `${basePath}.name`,
    required: `${basePath}.required`,
    unique: `${basePath}.unique`,
    description: `${basePath}.description`,
    position: `${basePath}.position`,
    propertyAttributeTypeId: `${basePath}.propertyAttributeTypeId`,
    propertySchemaId: `${basePath}.propertySchema.id`,
    propertyTypeId: `${basePath}.propertySchema.propertyTypeId`,
    selectOptions: `${basePath}.propertySchema.selectOptions`,
    urlAllowList: `${basePath}.propertySchema.urlAllowList`,
  };

  const requiredValue = watch(paths.required) as boolean | undefined;
  const uniqueValue = watch(paths.unique) as boolean | undefined;
  const attributeTypeIdValue = watch(paths.propertyAttributeTypeId) as PropertyAttributeTypeId | null | undefined;
  const propertyTypeIdValue = watch(paths.propertyTypeId) as PropertySchemaType;
  const nameValue = (watch(paths.name) as string) || "";
  const idValue = (watch(paths.id) as string | undefined) || undefined;

  const isLocked = isMandatoryUnsavedSchema(attributeTypeIdValue, propertyTypeIdValue, idValue);

  const nameError = getFieldState(paths.name, formState).error;

  // When attribute type changes to a mandatory one, force the corresponding property type.
  useEffect(() => {
    if (attributeTypeIdValue && MANDATORY_PROPERTY_MAPPINGS[attributeTypeIdValue]) {
      const mandatoryType = MANDATORY_PROPERTY_MAPPINGS[attributeTypeIdValue];
      setValue(paths.propertyTypeId, mandatoryType as PathValue<FieldValues, FieldPath<FieldValues>>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeTypeIdValue]);

  // Select-option sub-array
  const {
    fields: selectOptionFields,
    append: appendSelectOption,
    remove: removeSelectOption,
  } = useFieldArray({
    control,
    name: paths.selectOptions as ArrayPath<FieldValues>,
  });

  // URL-allow-list sub-array
  const {
    fields: urlAllowListFields,
    append: appendUrlAllowList,
    remove: removeUrlAllowList,
  } = useFieldArray({
    control,
    name: paths.urlAllowList as ArrayPath<FieldValues>,
  });

  const usedAttributeTypeIds = (
    (getValues("propertySchemaAssociations") as Array<{ propertyAttributeTypeId?: PropertyAttributeTypeId | null }>) ??
    []
  )
    .map((a) => a?.propertyAttributeTypeId)
    .filter(Boolean) as PropertyAttributeTypeId[];

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Compact / sort-mode summary
  if (sortMode) {
    return (
      <Card className="flex items-center gap-2 px-3 py-2">
        <GripVertical className="h-4 w-4 cursor-grab text-fg-3 active:cursor-grabbing" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onMoveUp}
          disabled={isFirst}
          aria-label={t("ui.moveUp", { defaultValue: "Move up" })}
        >
          <ChevronUp />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onMoveDown}
          disabled={isLast}
          aria-label={t("ui.moveDown", { defaultValue: "Move down" })}
        >
          <ChevronDown />
        </Button>
        <Badge tone="default">#{index}</Badge>
        <span className="truncate text-[13px] font-medium text-fg-1">{nameValue || `Property ${index}`}</span>
        {propertyTypeIdValue && <Badge tone="primary">{propertyTypeIdValue}</Badge>}
      </Card>
    );
  }

  return (
    <>
      <Card className="space-y-3">
        <input type="hidden" {...register(paths.id)} />
        <input type="hidden" {...register(paths.propertySchemaId)} />

        <div className="flex flex-wrap items-end gap-3">
          <Controller
            name={paths.position}
            control={control}
            render={({ field: { value } }) => <Badge tone="default">#{value ?? 0}</Badge>}
          />
          <div className="min-w-[200px] flex-1">
            <Label className="mb-1 block">{t("model.common.name")}</Label>
            <Input {...register(paths.name)} required />
            {nameError && <p className="mt-1 text-12-medium text-danger">{nameError.message}</p>}
          </div>
          <div>
            <Label className="mb-1 block">{t("model.propertySchema.propertyTypeId")}</Label>
            <PropertyTypeSelect
              value={getValues(paths.propertyTypeId) as string}
              onChange={(v) => setValue(paths.propertyTypeId, v as PathValue<FieldValues, FieldPath<FieldValues>>)}
              supportedTypes={supportedPropertyTypes}
              disabled={isLocked}
            />
          </div>
          {showPropertyAttributeTypeId && (
            <div>
              <Label className="mb-1 block">{t("model.propertySchemaAssociation.propertyAttributeTypeId")}</Label>
              <Controller
                name={paths.propertyAttributeTypeId}
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                    disabled={isLocked}
                  >
                    <SelectTrigger className="h-9 w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t("ui.none")}</SelectItem>
                      {propertyAttributeTypes.map((type) => (
                        <SelectItem
                          key={type}
                          value={type}
                          disabled={type !== field.value && usedAttributeTypeIds.includes(type)}
                        >
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setConfirmOpen(true)}
            disabled={isLocked}
            aria-label={t("ui.delete")}
            className="text-danger"
          >
            <Trash2 />
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <div>
            <Label className="mb-1 block">{t("model.common.description")}</Label>
            <Textarea {...register(paths.description)} rows={2} />
          </div>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: label wraps the Checkbox; biome can't see through the component */}
          <label className="inline-flex items-center gap-2 pb-1.5 text-[13px] text-fg-2">
            <Checkbox
              checked={!!requiredValue}
              onCheckedChange={(v) =>
                setValue(paths.required, (v === true) as PathValue<FieldValues, FieldPath<FieldValues>>, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
            <span>{t("model.propertySchemaAssociation.required")}</span>
          </label>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: label wraps the Checkbox; biome can't see through the component */}
          <label className="inline-flex items-center gap-2 pb-1.5 text-[13px] text-fg-2">
            <Checkbox
              checked={!!uniqueValue}
              onCheckedChange={(v) =>
                setValue(paths.unique, (v === true) as PathValue<FieldValues, FieldPath<FieldValues>>, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
            <span>{t("model.propertySchemaAssociation.unique")}</span>
          </label>
        </div>

        {(propertyTypeIdValue === "select" || propertyTypeIdValue === "multiSelect") && (
          <div className="rounded-[var(--radius-md)] border border-dashed border-border-default p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[13px] font-semibold text-fg-1">
                {t("model.propertySchema.selectOptions.modelName")}
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => appendSelectOption({ value: "" } as never)}
              >
                {t("feature.dataImportProjects.form.addSelectOption")}
              </Button>
            </div>
            {selectOptionFields.map((field, i) => (
              <SelectOptionFormInput
                key={field.id}
                register={register}
                basePath={paths.selectOptions}
                index={i}
                onRemove={removeSelectOption}
              />
            ))}
          </div>
        )}

        {propertyTypeIdValue === "url" && (
          <div className="rounded-[var(--radius-md)] border border-dashed border-border-default p-3">
            <div className="flex items-center justify-between">
              <h3
                className="text-[13px] font-semibold text-fg-1"
                title={t("model.propertySchema.urlAllowList.modelNameInfo")}
              >
                {t("model.propertySchema.urlAllowList.modelName")}
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => appendUrlAllowList({ value: "", regex: "" } as never)}
              >
                {t("feature.dataImportProjects.form.addUrlAllowListItem")}
              </Button>
            </div>
            {urlAllowListFields.map((field, i) => (
              <URLAllowListFormInput
                key={field.id}
                register={register}
                basePath={paths.urlAllowList}
                index={i}
                onRemove={removeUrlAllowList}
              />
            ))}
          </div>
        )}
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("ui.deleteX", { x: nameValue || `Property #${index}` })}</AlertDialogTitle>
            <AlertDialogDescription>{t("ui.areYouSure", { defaultValue: "Are you sure?" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onRemove(index);
                setConfirmOpen(false);
              }}
            >
              {t("ui.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
